import type { TriState } from '@/components/TriStateCheckbox';
import type { Promotion } from '@restaurant/api-client';

export interface TargetSelection {
  categoryIds: string[];
  productIds: string[];
}

export interface TargetCategory {
  id: string;
  productIds: string[];
}

export type PromotionTargetInput = { categoryId: string } | { productId: string };

export const emptySelection = (): TargetSelection => ({ categoryIds: [], productIds: [] });

export function selectionFromTargets(targets: Promotion['targets']): TargetSelection {
  return {
    categoryIds: targets.flatMap((t) => (t.categoryId ? [t.categoryId] : [])),
    productIds: targets.flatMap((t) => (t.productId ? [t.productId] : [])),
  };
}

export function categoryState(selection: TargetSelection, category: TargetCategory): TriState {
  if (selection.categoryIds.includes(category.id)) return 'checked';
  const checked = category.productIds.filter((id) => selection.productIds.includes(id)).length;
  if (checked === 0) return 'empty';
  return checked === category.productIds.length ? 'checked' : 'partial';
}

export function checkedProductCount(selection: TargetSelection, category: TargetCategory): number {
  if (selection.categoryIds.includes(category.id)) return category.productIds.length;
  return category.productIds.filter((id) => selection.productIds.includes(id)).length;
}

export function isProductChecked(
  selection: TargetSelection,
  category: TargetCategory,
  productId: string,
): boolean {
  return selection.categoryIds.includes(category.id) || selection.productIds.includes(productId);
}

export function toggleCategory(
  selection: TargetSelection,
  category: TargetCategory,
): TargetSelection {
  const cleared = {
    categoryIds: selection.categoryIds.filter((id) => id !== category.id),
    productIds: selection.productIds.filter((id) => !category.productIds.includes(id)),
  };
  return categoryState(selection, category) === 'checked'
    ? cleared
    : { ...cleared, categoryIds: [...cleared.categoryIds, category.id] };
}

export function toggleProduct(
  selection: TargetSelection,
  category: TargetCategory,
  productId: string,
): TargetSelection {
  if (selection.categoryIds.includes(category.id)) {
    return {
      categoryIds: selection.categoryIds.filter((id) => id !== category.id),
      productIds: [
        ...selection.productIds,
        ...category.productIds.filter(
          (id) => id !== productId && !selection.productIds.includes(id),
        ),
      ],
    };
  }
  return {
    categoryIds: selection.categoryIds,
    productIds: selection.productIds.includes(productId)
      ? selection.productIds.filter((id) => id !== productId)
      : [...selection.productIds, productId],
  };
}

export function targetsFromSelection(
  selection: TargetSelection,
  catalog: TargetCategory[],
): PromotionTargetInput[] {
  const whole = new Set(selection.categoryIds);
  const coveredProducts = new Set(
    catalog.filter((category) => whole.has(category.id)).flatMap((c) => c.productIds),
  );
  return [
    ...[...whole].map((categoryId) => ({ categoryId })),
    ...[...new Set(selection.productIds)]
      .filter((productId) => !coveredProducts.has(productId))
      .map((productId) => ({ productId })),
  ];
}
